/**
 * 服务管理Modal配置
 * 包含创建和编辑服务的表单配置
 */
import type { FormItemConfig } from "@ms/shared-components/comps/CompsForm";
import { GenericModalIconSelect } from "@ms/shared-components";

/**
 * 构建基础服务表单字段配置
 * @returns 表单字段配置数组
 */
export const buildBaseServiceFormFields = (): FormItemConfig[] => [
  {
    name: "name",
    label: "服务名称",
    type: "input",
    rules: [
      { required: true, message: "服务名称不能为空" },
      {
        pattern: /^[a-z0-9_-]{2,16}$/,
        message: "服务名称只能包含小写字母、数字、下划线和连字符，长度2-16个字符",
      },
    ],
    props: {
      placeholder: "请输入服务名称，如：user-service、order-api",
      autoFocus: true,
    },
  },
  {
    name: "displayName",
    label: "显示名称",
    type: "input",
    rules: [
      { required: true, message: "显示名称不能为空" },
      { max: 100, message: "显示名称不能超过100个字符" },
    ],
    props: {
      placeholder: "请输入显示名称，如：用户管理、订单系统",
    },
  },
  {
    name: "description",
    label: "服务描述",
    type: "textarea",
    rules: [{ max: 500, message: "服务描述不能超过500个字符" }],
    props: {
      placeholder: "请输入服务描述信息",
    },
  },
  {
    name: "icon",
    label: "图标选择",
    type: "custom",
    customComponent: GenericModalIconSelect,
    rules: [{ required: true, message: "请选择图标" }],
    props: {
      placeholder: "请选择图标",
    },
  },
  {
    name: "routePath",
    label: "路由路径",
    type: "input",
    rules: [
      { required: true, message: "路由路径不能为空" },
      {
        pattern: /^\/[a-zA-Z0-9_-]*$/,
        message: "路由路径必须以/开头，只能包含字母、数字、下划线和连字符",
      },
    ],
    props: {
      placeholder: "请输入路由路径，如：/msadmin、/users",
    },
  },
  {
    name: "menuOrder",
    label: "菜单排序",
    type: "number",
    rules: [
      { required: true, message: "菜单排序不能为空" },
      { type: "number", min: 0, message: "菜单排序必须大于等于0" },
    ],
    props: {
      placeholder: "请输入菜单排序数字，数字越小排序越靠前",
      min: 0,
      max: 999,
    },
  },
  {
    name: "url",
    label: "前端路径",
    type: "input",
    rules: [
      {
        pattern: /^(https?:\/\/)?[a-zA-Z0-9.-]+(:\d+)?(\/.*)?$/,
        message: "请输入有效的前端访问地址，如：http://localhost:30091 或 https://admin.example.com",
      },
    ],
    props: {
      placeholder: "请输入前端访问地址，如：http://localhost:30091",
    },
  },
  {
    name: "backendUrl",
    label: "后端路径",
    type: "input",
    rules: [
      {
        pattern: /^(https?:\/\/)?[a-zA-Z0-9.-]+(:\d+)?(\/.*)?$/,
        message: "请输入有效的后端API地址，如：http://localhost:50090 或 http://api.example.com",
      },
    ],
    props: {
      placeholder: "请输入后端API地址，如：http://localhost:50090",
    },
  },
  {
    name: "service_secret",
    label: "服务秘钥",
    type: "input",
    rules: [
      { max: 255, message: "服务秘钥不能超过255个字符" },
    ],
    props: {
      placeholder: "请输入服务秘钥（可选）",
    },
  },
  {
    name: "health_check_url",
    label: "健康检查地址",
    type: "input",
    rules: [
      {
        pattern: /^(\/|https?:\/\/).*/,
        message: "请输入有效的健康检查地址，如：/health 或 http://localhost:3001/health",
      },
    ],
    props: {
      placeholder: "请输入健康检查地址，如：/health（推荐使用相对路径）",
    },
  },
  {
    name: "is_menu_item",
    label: "显示在菜单中",
    type: "switch",
    props: {
      checkedChildren: "是",
      unCheckedChildren: "否",
    },
  },
  {
    name: "requires_auth",
    label: "需要认证",
    type: "switch",
    props: {
      checkedChildren: "是",
      unCheckedChildren: "否",
    },
  },
  // {
  //   name: "is_external",
  //   label: "外部服务",
  //   type: "switch",
  //   props: {
  //     checkedChildren: "是",
  //     unCheckedChildren: "否",
  //   },
  // },
  {
    name: "service_status",
    label: "服务状态",
    type: "select",
    options: [
      { label: "运行中", value: 1 },
      { label: "已停止", value: 2 },
      { label: "维护中", value: 3 },
    ],
    rules: [{ required: true, message: "请选择服务状态" }],
    props: {
      placeholder: "请选择服务状态",
    },
  },
  {
    name: "service_type",
    label: "服务类型",
    type: "select",
    options: [
      { label: "系统", value: 0 },
      { label: "后台", value: 1 },
      { label: "中台", value: 2 },
      { label: "前台", value: 3 },
    ],
    rules: [{ required: true, message: "请选择服务类型" }],
    props: {
      placeholder: "请选择服务类型",
    },
  },
];

/**
 * 表单弹窗配置
 */
export const FORM_MODAL_CONFIG = {
  CREATE: {
    title: "创建微服务",
    width: 600,
    height: "auto",
    contentPadding: {
      vertical: 16,
      horizontal: 0,
    },
    okText: "确定",
    cancelText: "取消",
  },
  EDIT: {
    title: "编辑微服务",
    width: 600,
    height: "auto",
    contentPadding: {
      vertical: 16,
      horizontal: 0,
    },
    okText: "确定",
    cancelText: "取消",
  },
} as const;

// 服务 Modal Footer 按钮配置接口
export interface ServiceModalFooterButtonsProps {
  submitting: boolean;
  onCancel: () => void;
  onSubmit: () => void;
}

/**
 * 创建服务 Modal Footer 按钮配置
 * @param props 按钮配置参数
 * @returns Footer 按钮配置数组
 */
export const createServiceModalFooterButtons = ({
  submitting,
  onCancel,
  onSubmit,
}: ServiceModalFooterButtonsProps) => [
    {
      key: "cancel",
      text: FORM_MODAL_CONFIG.CREATE.cancelText,
      type: "default" as const,
      onClick: onCancel,
    },
    {
      key: "submit",
      text: FORM_MODAL_CONFIG.CREATE.okText,
      type: "primary" as const,
      loading: submitting,
      onClick: onSubmit,
    },
  ];

/**
 * 编辑服务 Modal Footer 按钮配置
 * @param props 按钮配置参数
 * @returns Footer 按钮配置数组
 */
export const editServiceModalFooterButtons = ({
  submitting,
  onCancel,
  onSubmit,
}: ServiceModalFooterButtonsProps) => [
    {
      key: "cancel",
      text: FORM_MODAL_CONFIG.EDIT.cancelText,
      type: "default" as const,
      onClick: onCancel,
    },
    {
      key: "submit",
      text: FORM_MODAL_CONFIG.EDIT.okText,
      type: "primary" as const,
      loading: submitting,
      onClick: onSubmit,
    },
  ];

